import React, { useState, useCallback } from 'react';
import { GripVertical, Maximize2 } from 'lucide-react';
import { PanelMenu } from '../ui/PanelMenu';
import { ExpandedPanelModal } from '../ui/ExpandedPanelModal';
import { useToast, toastHelpers } from '../ui/Toast';
import { ColorLinkDot } from '../ui/ColorLinkDot';

interface PanelProps {
  id?: string;
  title: string;
  subtitle?: string;
  status?: 'live' | 'delayed' | 'static';
  children: React.ReactNode;
  className?: string;
  // Drag & drop
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  isDragOver?: boolean;
  // Data for export/copy
  data?: Record<string, unknown>[];
  // Expand content (can be different from children)
  expandedContent?: React.ReactNode;
  // External minimize control
  isMinimized?: boolean;
  onMinimize?: () => void;
  // Panel linking
  linkable?: boolean;
}

export const Panel: React.FC<PanelProps> = ({
  id,
  title,
  subtitle,
  status = 'static',
  children,
  className = '',
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragOver = false,
  data,
  expandedContent,
  isMinimized: externalIsMinimized,
  onMinimize: externalOnMinimize,
  linkable = true,
}) => {
  // Use external minimize state if provided, otherwise use internal state
  const [internalIsMinimized, setInternalIsMinimized] = useState(false);
  const isMinimized = externalIsMinimized !== undefined ? externalIsMinimized : internalIsMinimized;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Toast might not be available if ToastProvider isn't mounted
  let showToast: ReturnType<typeof useToast>['showToast'] | null = null;
  try {
    const toastContext = useToast();
    showToast = toastContext.showToast;
  } catch {
    // Toast provider not available - that's ok
  }

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleMinimize = useCallback(() => {
    if (externalOnMinimize) {
      externalOnMinimize();
    } else {
      setInternalIsMinimized(!internalIsMinimized);
    }
  }, [externalOnMinimize, internalIsMinimized]);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    // Simulate refresh
    setTimeout(() => {
      setIsRefreshing(false);
      if (showToast) {
        toastHelpers.refreshed(showToast);
      }
    }, 800);
  }, [showToast]);

  const handleCopy = useCallback(() => {
    if (data) {
      const text = JSON.stringify(data, null, 2);
      navigator.clipboard.writeText(text).then(() => {
        if (showToast) {
          toastHelpers.copied(showToast);
        }
      });
    }
  }, [data, showToast]);

  const handleExport = useCallback((format: 'csv' | 'json') => {
    if (!data || data.length === 0) return;

    let content: string;
    let mimeType: string;
    let extension: string;

    if (format === 'json') {
      content = JSON.stringify(data, null, 2);
      mimeType = 'application/json';
      extension = 'json';
    } else {
      // CSV export
      const headers = Object.keys(data[0]);
      const rows = data.map(row =>
        headers.map(header => {
          const value = row[header];
          const stringValue = value === null || value === undefined ? '' : String(value);
          // Escape quotes and wrap in quotes if contains comma
          if (stringValue.includes(',') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      );
      content = [headers.join(','), ...rows].join('\n');
      mimeType = 'text/csv';
      extension = 'csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    if (showToast) {
      toastHelpers.exported(showToast, format);
    }
  }, [data, title, showToast]);

  // Drag handlers
  const handleDragStart = (e: React.DragEvent) => {
    if (id) {
      e.dataTransfer.setData('text/plain', id);
      e.dataTransfer.effectAllowed = 'move';
    }
    onDragStart?.(e);
  };

  return (
    <>
      <div
        className={`bg-terminal-panel flex flex-col overflow-hidden h-full transition-all duration-200 ${className} ${
          isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''
        } ${isRefreshing ? 'animate-pulse' : ''}`}
        draggable={draggable}
        onDragStart={draggable ? handleDragStart : undefined}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
      >
        {/* Header */}
        <div className="min-h-[44px] md:h-6 px-2 flex items-center justify-between bg-terminal-header border-b border-terminal-border shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {draggable && (
              <div className="cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-400 shrink-0 p-1 md:p-0">
                <GripVertical size={16} className="md:w-3 md:h-3" />
              </div>
            )}
            {status === 'live' && (
              <span className="w-2 h-2 md:w-1.5 md:h-1.5 rounded-full bg-data-green blink shrink-0" />
            )}
            {status === 'delayed' && (
              <span className="w-2 h-2 md:w-1.5 md:h-1.5 rounded-full bg-data-amber shrink-0" />
            )}
            {linkable && id && <ColorLinkDot panelId={id} size="sm" />}
            <span className="text-data-amber text-xs md:text-[10px] font-semibold uppercase truncate">
              {title}
            </span>
            {subtitle && (
              <span className="text-gray-500 text-[10px] truncate hidden sm:block">
                | {subtitle}
              </span>
            )}
          </div>

          <div className="flex items-center gap-0 md:gap-1 shrink-0">
            {/* Expand to fullscreen modal */}
            <button
              onClick={handleExpand}
              className="min-w-[36px] min-h-[36px] md:min-w-0 md:min-h-0 flex items-center justify-center text-gray-600 hover:text-gray-400 active:bg-terminal-border/50 md:active:bg-transparent transition-colors md:p-0.5 rounded hover:bg-terminal-border"
              aria-label="Expand panel to fullscreen"
            >
              <Maximize2 size={14} className="md:w-3 md:h-3" />
            </button>

            {/* Menu */}
            <PanelMenu
              onExpand={handleExpand}
              onMinimize={handleMinimize}
              onRefresh={handleRefresh}
              onCopy={data ? handleCopy : undefined}
              onExport={data ? handleExport : undefined}
              isMinimized={isMinimized}
            />
          </div>
        </div>

        {/* Content */}
        <div
          className={`flex-1 overflow-auto p-1.5 min-h-0 transition-all duration-200 ${
            isMinimized ? 'h-0 p-0 overflow-hidden' : ''
          }`}
        >
          {children}
        </div>
      </div>

      {/* Expanded Modal */}
      <ExpandedPanelModal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        title={title}
        subtitle={subtitle}
      >
        {expandedContent || children}
      </ExpandedPanelModal>
    </>
  );
};

export default Panel;
